"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export interface GuestCheckoutContact {
  email: string;
  phone?: string;
}

export interface GuestContactPrompt {
  open: boolean;
  initial: GuestCheckoutContact;
  onSubmit: (contact: GuestCheckoutContact) => void;
  onDismiss: () => void;
}

type ContactResolver = (contact: GuestCheckoutContact | null) => void;

const STORAGE_KEY = "repackify.guest-checkout-contact";

export function readRememberedContact(): GuestCheckoutContact {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === "object" && "email" in parsed) {
      const email = typeof parsed.email === "string" ? parsed.email : "";
      const phone =
        "phone" in parsed && typeof parsed.phone === "string"
          ? parsed.phone
          : undefined;
      return { email, phone };
    }
  } catch {
    return { email: "" };
  }
  return { email: "" };
}

export function rememberContact(contact: GuestCheckoutContact) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(contact));
  } catch {
    return;
  }
}

export function useGuestContactPrompt() {
  const pending = useRef<ContactResolver | null>(null);
  const [open, setOpen] = useState(false);
  const [initial, setInitial] = useState<GuestCheckoutContact>({ email: "" });

  useEffect(
    () => () => {
      pending.current?.(null);
      pending.current = null;
    },
    [],
  );

  const request = useCallback(
    (collected?: GuestCheckoutContact) =>
      new Promise<GuestCheckoutContact | null>((resolve) => {
        if (collected) {
          resolve(collected);
          return;
        }
        pending.current?.(null);
        pending.current = resolve;
        setInitial(readRememberedContact());
        setOpen(true);
      }),
    [],
  );

  const settle = (contact: GuestCheckoutContact | null) => {
    const resolve = pending.current;
    pending.current = null;
    setOpen(false);
    resolve?.(contact);
    if (contact) rememberContact(contact);
  };

  const prompt: GuestContactPrompt = {
    open,
    initial,
    onSubmit: settle,
    onDismiss: () => settle(null),
  };
  return { request, prompt };
}
